import { useQuery } from '@tanstack/vue-query';
import { request } from '@/api/request';
import { mergeQueryOptions, type QueryOptions } from '@/modules/query/utils';
import type { DatasetRequest, DatasetResponse } from '@@/packages/shared/libs/search/types';
import type {
  EmergingThreatSpecificQuestionAnswer,
  SpecificQuestion,
} from '@@/packages/shared/types/models/emergingThreats';
import {
  EMERGING_THREAT_CLIENT_METRICS_QUERY,
  EMERGING_THREAT_LIST_WITH_SUPPLIER_RESPONSE_QUERY,
  EMERGING_THREAT_SUPPLIER_POTENTIAL_THREAT_COUNT_QUERY,
  EMERGING_THREAT_SUPPLIER_QUESTION_ANSWERS_QUERY,
  EMERGING_THREAT_SUPPLIERS_QUERY,
  EMERGING_THREATS_CLIENTS_QUERY,
  EMERGING_THREATS_SUPPLIER_QUESTION_ANSWERS_QUERY,
} from './queryKeys';

type EmergingThreatsSupplierResponse_Answer = {
  answer: string;
  hasNotes: boolean;
  hasEvidence: boolean;
};

export type EmergingThreatsSupplierResponse = {
  id: string;
  name: string;
  logoUrl: string;
  location: string;
  criticality: string;
  confidentiality: string;
  sector: string;
  holdsPII: string;
  labels: string[];
  assignedUsers: string[];
  assignedToMe: boolean;
  responseStatus: string;
  respondedAt?: string;
  modifiedAt?: string;
  specificQuestions: Record<string, EmergingThreatsSupplierResponse_Answer>;
};

type SuppliersResponse = DatasetResponse & {
  suppliers: EmergingThreatsSupplierResponse[];
  totalSuppliers: number;
};

export const fetchClientEmergingThreatSupplierList = (id: string, payload: DatasetRequest) =>
  request<SuppliersResponse>('POST', `/emerging-threats/${id}/suppliers`, payload);

export type EmergingThreatForClientsList = {
  id: string;
  name: string;
  type: string;
  emergedAt: string;
  summary: string;
  guideLink: string;
  status: string;
  publishedAt: string;
  remediatingSuppliers: number;
  unansweredSuppliers: number;
  investigatingSuppliers: number;
  resolvedSuppliers: number;
  unaffectedSuppliers: number;
};

type EmergingThreatsListForClientsResponse = DatasetResponse & {
  totalEmergingThreats: number;
  emergingThreats: EmergingThreatForClientsList[];
};

export type SupplierPotentialThreatsCountResponse = {
  potentialThreats: number;
};

export type EmergingThreatListWithSupplierResponseItem = {
  id: string;
  name: string;
  type: string;
  emergedAt: string;
  summary: string;
  guideLink: string;
  status: string;
  publishedAt: string;
  responseStatus: string;
  responseModifedAt: string | null;
  specificQuestions: SpecificQuestion[] | null;
};

export const fetchEmergingThreatListWithSupplierResponse = (supplierID: string) =>
  request<EmergingThreatListWithSupplierResponseItem[]>(
    'GET',
    `/emerging-threats/supplier/${supplierID}/threat-list`,
  );

export type EmergingThreatsSupplierQuestionsAnswersResponse = Record<
  string,
  EmergingThreatSpecificQuestionAnswer
>;

export const fetchEmergingThreatsListForClients = (payload: DatasetRequest) =>
  request<EmergingThreatsListForClientsResponse>(
    'POST',
    '/emerging-threats/list-for-clients',
    payload,
  );

export function useEmergingThreatsClients(
  params: MaybeRefOrGetter<DatasetRequest>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [EMERGING_THREATS_CLIENTS_QUERY, toRef(params)] as const,
      async queryFn({ queryKey }) {
        const res = await fetchEmergingThreatsListForClients(queryKey[1]);
        return res.data;
      },
    }),
  );

  const emergingThreats = computed(() => query.data.value?.emergingThreats ?? []);
  const totalEmergingThreats = computed(() => query.data.value?.totalEmergingThreats ?? 0);
  const page = computed(() => query.data.value?.page ?? null);

  return { ...query, query, emergingThreats, totalEmergingThreats, page };
}

export function useEmergingThreatListWithSupplierResponse(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [EMERGING_THREAT_LIST_WITH_SUPPLIER_RESPONSE_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await fetchEmergingThreatListWithSupplierResponse(queryKey[1]);
        return res.data;
      },
    }),
  );

  return { ...query, threats: computed(() => query.data.value ?? []) };
}

export function useEmergingThreatsSupplierQuestionAnswers(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [EMERGING_THREATS_SUPPLIER_QUESTION_ANSWERS_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<EmergingThreatsSupplierQuestionsAnswersResponse>(
          'GET',
          `/emerging-threats/supplier/${queryKey[1]}/answers`,
        );
        return res.data;
      },
    }),
  );

  return { ...query, questionAnswers: computed(() => query.data.value ?? {}) };
}

type EmergingThreatClientMetricsResponse = {
  responseStatus: Record<string, { count: number; change: number }>;
  specificQuestionsAnswers: Record<string, { yes: number; no: number; unanswered: number }>;
};

export function useEmergingThreatClientMetrics(
  id: MaybeRefOrGetter<string>,
  criticality: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [EMERGING_THREAT_CLIENT_METRICS_QUERY, toRef(id), toRef(criticality)] as const,
      async queryFn({ queryKey }) {
        const res = await request<EmergingThreatClientMetricsResponse>(
          'GET',
          `/emerging-threats/${queryKey[1]}/metrics`,
          null,
          { criticality: queryKey[2] },
        );
        return res.data;
      },
    }),
  );

  return { ...query, query, metrics: query.data };
}

export function useEmergingThreatSupplierList(
  id: MaybeRefOrGetter<string>,
  params: MaybeRefOrGetter<DatasetRequest>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [EMERGING_THREAT_SUPPLIERS_QUERY, toRef(id), toRef(params)] as const,
      async queryFn({ queryKey }) {
        const res = await fetchClientEmergingThreatSupplierList(queryKey[1], queryKey[2]);
        return res.data;
      },
    }),
  );

  const suppliers = computed(() => query.data.value?.suppliers ?? []);
  const totalSuppliers = computed(() => query.data.value?.totalSuppliers ?? 0);
  const page = computed(() => query.data.value?.page ?? null);

  return { ...query, suppliers, page, totalSuppliers };
}

type EmergingThreatSupplierQuestionAnswersResponse = Record<
  string,
  EmergingThreatSpecificQuestionAnswer
>;

export function useEmergingThreatSupplierQuestionAnswers(
  threatID: MaybeRefOrGetter<string>,
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [
        EMERGING_THREAT_SUPPLIER_QUESTION_ANSWERS_QUERY,
        toRef(threatID),
        toRef(supplierID),
      ] as const,
      async queryFn({ queryKey }) {
        const res = await request<EmergingThreatSupplierQuestionAnswersResponse>(
          'GET',
          `/emerging-threats/${queryKey[1]}/supplier/${queryKey[2]}/answers`,
        );
        return res.data;
      },
    }),
  );

  return { ...query, questionAnswers: computed(() => query.data.value ?? {}) };
}

export function useSupplierPotentialThreatsCount(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [EMERGING_THREAT_SUPPLIER_POTENTIAL_THREAT_COUNT_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<SupplierPotentialThreatsCountResponse>(
          'GET',
          `/emerging-threats/supplier/${queryKey[1]}/potential-threats`,
        );
        return res.data;
      },
    }),
  );

  const potentialThreats = computed(() => query.data.value?.potentialThreats ?? 0);

  return { ...query, potentialThreats };
}
